import {
  GoogleAuthProvider,
  getAuth,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut,
  linkWithCredential,
  AuthCredential,
  UserCredential,
  getAdditionalUserInfo,
} from 'firebase/auth';

import {
  createCustomToken,
  getUser,
  updateUserName,
  verifyEmail,
} from '@api/userApi';
import { ActionType, provider } from '@/types/auth';
import { sharedData } from '@shares/dataManager';
import { PAGE_PATHS } from '@constants/url';
import { ACTION_TYPES } from '@/constants/actions';
import getLastVisitedService from '@/utils/getLastVisitedService';
import { CAMERAFI_STUDIO } from '@/constants/services';
import getGaClientToken from '@/utils/getGaClientToken';
import { isIOS } from '@/utils/uaParser';

const auth = getAuth();

const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.setCustomParameters({ prompt: 'select_account' });
const facebookAuthProvider = new FacebookAuthProvider();
const appleAuthProvider = new OAuthProvider('apple.com');
appleAuthProvider.addScope('email');
appleAuthProvider.addScope('name');
const kakaoAuthProvider = new OAuthProvider('oidc.kakao');
kakaoAuthProvider.addScope('account_email');
kakaoAuthProvider.addScope('openid');
kakaoAuthProvider.setCustomParameters({
  prompt: 'consent',
});
const kakaoNativeAuthProvider = new OAuthProvider('oidc.kakao.native');
kakaoNativeAuthProvider.addScope('account_email');

const authProvidersMap: {
  [key in provider]: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider;
} = {
  'google.com': googleAuthProvider,
  'facebook.com': facebookAuthProvider,
  'apple.com': appleAuthProvider,
  'oidc.kakao': isIOS ? kakaoNativeAuthProvider : kakaoAuthProvider,
  'oidc.kakao.native': kakaoNativeAuthProvider,
};

//* 이메일은 사실상 대소문자 무시이며 프로바이더별 정규화가 달라
//* 비교 전 lower-case + trim으로 통일한다. 값이 없으면 빈 문자열을 반환.
const normalizeEmail = (email: string | null | undefined) =>
  email?.trim().toLowerCase() ?? '';

const linkAccountWithPopup = async (
  pendingCred: AuthCredential,
  signInMethod: provider,
  expectedEmail: string,
) => {
  const result = await signInWithPopup(auth, authProvidersMap[signInMethod]);

  const normalizedResultEmail = normalizeEmail(result.user.email);
  const normalizedExpectedEmail = normalizeEmail(expectedEmail);

  if (
    !normalizedResultEmail ||
    !normalizedExpectedEmail ||
    normalizedResultEmail !== normalizedExpectedEmail
  ) {
    await signOut(auth);
    return null;
  }

  return await linkWithCredential(result.user, pendingCred);
};

const getIsNewUser = (userCredential: UserCredential) => {
  const additionalUserInfo = getAdditionalUserInfo(userCredential);

  let isNewUser = false;
  if (additionalUserInfo) isNewUser = additionalUserInfo.isNewUser;

  return isNewUser;
};

const idTokenHandler = async ({
  idToken,
  actionType,
  nickname,
  isNewUser,
}: {
  idToken: string;
  actionType: keyof ActionType;
  nickname?: string;
  isNewUser?: boolean;
}) => {
  const gaClientToken = getGaClientToken();

  try {
    const {
      data: { token: customToken },
    } = await createCustomToken({
      idToken,
      params: {
        lastVisitedService:
          sharedData.queries.platform !== null &&
          getLastVisitedService(sharedData.queries.platform) === CAMERAFI_STUDIO
            ? CAMERAFI_STUDIO
            : undefined,
        isNewUser: isNewUser ? 'true' : undefined,
        gaClientId: gaClientToken,
      },
    });

    if (actionType === ACTION_TYPES.EMAIL_SIGNUP && nickname) {
      await updateUserName({
        idToken,
        nickname,
      });

      await verifyEmail(idToken);
    }

    if (sharedData.queries.platform === 'playStore') {
      const {
        data: { uid, userName },
      } = await getUser(idToken);

      let url = new URL(PAGE_PATHS.DELETE_AGREEMENT, window.location.href);
      url.searchParams.append('platform', 'playStore');
      url.searchParams.append('uid', uid);
      url.searchParams.append('userName', userName);
      url.searchParams.append('api', idToken);

      window.location.href = url.href;
    }

    return {
      customToken,
    };
  } catch (error) {
    console.error('Error in idTokenHandler:', error);
    return { customToken: '' };
  }
};

// 카메라파이 쇼핑에서 통합로그인 페이지를 사용하는 경우
const isCalledFromParentSite = !sharedData.queries.callbackUrl && window.opener;

export {
  appleAuthProvider,
  auth,
  authProvidersMap,
  facebookAuthProvider,
  getIsNewUser,
  googleAuthProvider,
  idTokenHandler,
  linkAccountWithPopup,
  isCalledFromParentSite,
  kakaoAuthProvider,
  kakaoNativeAuthProvider,
};
